import { AxiosInstance } from 'axios';
import { DeliveryNoteMailConfiguration, DocumentBucket, mapReplacer, readFileFromMinio } from '..';
import i18n from '../../../client/src/i18n/i18n';
import { NotificationType } from '../notification/notification.types';
import { generateNotification } from '../notification/notification.util';
import { DeliveryNote, DeliveryNoteConfig } from './DeliveryNote.types';

/**
 * API Method to create a new deliveryNote
 * @param axios instance of axios
 * @param deliveryNote to create
 * @returns saved deliveryNote or undefined
 */
export const saveNewOrUpdatedDeliveryNote = async (
  axios: AxiosInstance,
  deliveryNote: DeliveryNote,
): Promise<DeliveryNote | undefined> => {
  return axios
    .post('/delivery/', JSON.stringify(deliveryNote, mapReplacer))
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.createNewDeliveryNote'),
      });
      console.error('Error while saving deliveryNote', exc);
      return undefined;
    });
};
/**
 * API Method to update an existing deliveryNote
 * @param axios
 * @param deliveryNote
 * @returns
 */
export const updateDeliveryNote = async (
  axios: AxiosInstance,
  deliveryNote: DeliveryNote,
): Promise<DeliveryNote | undefined> => {
  return axios
    .post('/delivery/update', JSON.stringify(deliveryNote, mapReplacer))
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.updateDeliveryNote'),
      });
      console.error('Error while saving deliveryNote', exc);
      return undefined;
    });
};

/**
 * API Method to get the actual pdf file of a deliveryNote
 * @param axios instance of axios
 * @param path the filePath for minio
 * @returns The file as a blob or undefined on error
 */
export const getDeliveryNoteDocument = async (
  axios: AxiosInstance,
  path: string,
): Promise<Blob | undefined> => {
  return readFileFromMinio(axios, DocumentBucket.PROJECT, path, 'application/pdf')
    .then((loadedFile) => loadedFile)
    .catch((exc) => {
      console.error('Error while downloading deliveryNote document', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.downloadDeliveryNote'),
      });
      return undefined;
    });
};
/**
 * API method to delete a deliveryNote instance
 * @param axios instance of axios
 * @param id the id of the deliveryNote to delete
 * @returns true or false for success and failure
 */
export const deleteDeliveryNote = async (axios: AxiosInstance, id: string): Promise<boolean> => {
  return axios
    .post('/delivery/delete', id, { headers: { 'Content-Type': 'text/plain' } })
    .then((res) => res.status === 200)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.createNewDeliveryNote'),
      });
      console.error('Error while saving deliveryNote', exc);
      return false;
    });
};

/**
 * API Method to create  a delivery note as a pdf document without saving
 * @param axios instance of axios
 * @param config neccessary config to crete a deliveryNote
 * @returns Blob of the created document or undefined on error
 */
export const createTransientDeliveryNotePdf = async (
  axios: AxiosInstance,
  config: DeliveryNoteConfig,
): Promise<Blob | undefined> => {
  return axios
    .post('/export/pdf/delivery', JSON.stringify(config, mapReplacer), {
      responseType: 'blob',
    })
    .then((res) => new Blob([res.data], { type: 'application/pdf' }))
    .catch((exc) => {
      console.error('Error while creating deliveryNote', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.createTransientDeliveryNote'),
      });
      return undefined;
    });
};

/**
 * API Method to create and save a deliveryNote as a pdf document
 * @param axios instance of axios
 * @param config neccessary config to create the note
 * @returns Promise containing a boolean value if the creation was successfull
 */
export const createAndSaveDeliveryNotePdf = async (
  axios: AxiosInstance,
  config: DeliveryNoteConfig,
): Promise<DeliveryNote | undefined> => {
  return axios
    .post('/export/pdf/delivery/save', JSON.stringify(config, mapReplacer))
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while saving document', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.createAndSaveDeliveryNote'),
      });
      return undefined;
    });
};

/**
 * API Method to create a deliveryNote and ssend it per mail
 * @param axios instance of axios
 * @param config mailConfiguration with the neccessary data
 * @returns Promise containing a boolean depending of the success
 */
export const createAndSendDeliveryNote = async (
  axios: AxiosInstance,
  config: DeliveryNoteMailConfiguration,
): Promise<boolean> => {
  return axios
    .post('/mail/deliverynote', JSON.stringify(config, mapReplacer))
    .then((res) => res.status === 200)
    .catch((exc) => {
      console.error('Error while sending mail', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.createAndSendDeliveryNote'),
      });
      return false;
    });
};

/**
 * API method to load all deliveryNotes for the current selected offices
 * @param axios instance of axios
 * @returns List of all found deliveryNotes or empty array on error
 */
export const getAllDeliveryNotesForOffice = async (
  axios: AxiosInstance,
): Promise<DeliveryNote[]> => {
  return axios
    .get('/delivery/all')
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while loading deliveryNotes', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getAllDeliveryNotes'),
      });
    });
};

/**
 * API Method to load all deliveryNotes for a project
 * @param axios instance of axios
 * @param projectId id of the project to get the deliveryNotes
 * @returns Promise containing all found deliveryNotes
 */
export const getAllDeliveryNotesForProject = async (
  axios: AxiosInstance,
  projectId: string,
): Promise<DeliveryNote[]> => {
  return axios
    .get('/delivery/project', { params: { projectId } })
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while loading deliveryNotes for project', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getAllDeliveryNotes'),
      });
      return [];
    });
};
